import { Component, Input, Output, EventEmitter } from '@angular/core';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

/**
 * Generic component for adding pagination navigation links to a result set.
 *
 * @export
 * @class PaginatorComponent
 */
@Component({
    selector: 'app-paginator',
    templateUrl: './paginator.component.html',
    standalone: false
})
export class PaginatorComponent {
  /**
   * Total number of results.
   *
   * @type {number}
   * @memberof PaginatorComponent
   */
  @Input() public total: number | undefined;

  /**
   * Results to show per page.
   *
   * @memberof PaginatorComponent
   */
  @Input() public perPage = 50;

  /**
   * Current page.
   *
   * @memberof PaginatorComponent
   */
  @Input() public current: number | undefined = 1;

  /**
   * Number of pages to show either side of the current page in the rendered selectable range.
   *
   * @memberof PaginatorComponent
   */
  @Input() public range = 1;

  /**
   * Type of response when moving between pages — either a skip value or a page number.
   *
   * Page number is an explicit page to show of results.
   * Skip value is the result count up until the last result shown within the current result set.
   *
   * @type {('skipValue' | 'pageNumber')}
   * @memberof PaginatorComponent
   */
  @Input() public response: 'skipValue' | 'pageNumber' = 'pageNumber';

  /**
   * Show stats panel at the bottom of the paginator, for debugging purposes.
   *
   * @memberof PaginatorComponent
   */
  @Input() public showStats = false;

  /**
   * Show any buttons as icons, not text.
   *
   * @memberof PaginatorComponent
   */
  @Input() public showButtonsAsIcons = true;

  /**
   * Event trigger for a page change.
   *
   * @private
   * @memberof PaginatorComponent
   */
  @Output() private change = new EventEmitter<number>();

  /**
   * Event trigger for navigating to the previous page.
   *
   * @private
   * @memberof PaginatorComponent
   */
  @Output() private previous = new EventEmitter<number>();

  /**
   * Event trigger for navigating to the next page.
   *
   * @private
   * @memberof PaginatorComponent
   */
  @Output() private next = new EventEmitter<number>();

  /**
   * Event trigger for navigating to a specifically request page.
   *
   * @private
   * @memberof PaginatorComponent
   */
  @Output() private goTo = new EventEmitter<number>();

  /**
   * Back icon.
   *
   * @memberof PaginatorComponent
   */
  faChevronLeft = faChevronLeft;

  /**
   * Next icon.
   *
   * @memberof PaginatorComponent
   */
  faChevronRight = faChevronRight;

  /**
   * Get the total number of pages available.
   *
   * @return {number} Number of pages.
   * @memberof PaginatorComponent
   */
  public getPageCount() {
    if (!this.total || this.total === 0) {
      return 1;
    } else {
      return Math.ceil(this.total / this.perPage);
    }
  }

  /**
   * Get the page number range for rendering.
   *
   * @return {*}  {string[]} Array of range values.
   * @memberof PaginatorComponent
   */
  public getPageNumberRange(): string[] {
    return this.buildRange();
  }

  /**
   * Get the pagination response based on configuration (page number or skip value).
   *
   * @return {*}  {number} Page number or skip value, depending on configuration.
   * @memberof PaginatorComponent
   */
  public getResponse(): number {
    if (this.response === 'skipValue') {
      return this.getSkipValue();
    } else {
      return this.current ?? 1;
    }
  }

  /**
   * Get the pagination skip value.
   *
   * This is an alternative way to find which page to show results for, instead of page x it is get results after result number y (skipping anything before y).
   *
   * @return {*} {number} Skip value.
   * @memberof PaginatorComponent
   */
  public getSkipValue(): number {
    if (this.current === 1) {
      return 0;
    }

    return ((this.current ?? 1) - 1) * this.perPage;
  }

  /**
   * Jump to a specific result page.
   *
   * @param {(number | string)} i
   * @memberof PaginatorComponent
   */
  public goToPage(i: number | string) {
    this.current = +i;
    this.goTo.emit(this.getResponse());
    this.change.emit(this.getResponse());
  }

  /**
   * Jump to the previous result page.
   *
   * @memberof PaginatorComponent
   */
  public goToPreviousPage() {
    this.current = (this.current ?? 1) - 1;
    this.previous.emit(this.getResponse());
    this.change.emit(this.getResponse());
  }

  /**
   * Jump to the next result page.
   *
   * @memberof PaginatorComponent
   */
  public goToNextPage() {
    this.current = (this.current ?? 1) + 1;
    this.next.emit(this.getResponse());
    this.change.emit(this.getResponse());
  }

  /**
   * Ask the user which result page to jump to.
   *
   * @memberof PaginatorComponent
   */
  public promptForPageNumber() {
    const pageNumber = prompt('Enter page number to go to...');

    if (pageNumber != null && +pageNumber > 1 && +pageNumber <= this.getPageCount()) {
      this.goToPage(+pageNumber);
    }
  }

  /**
   * Build a selectable page range around the currently selected page number.
   *
   * @private
   * @return {*} {string[]} Array of range values.
   * @memberof PaginatorComponent
   */
  private buildRange(): string[] {
    const pageCount = this.getPageCount();

    const pageRange = new Array<number>();
    const pageRangeWithDots = new Array<string>();

    const left = this.range ? (this.current ?? 1) - this.range : 0;
    const right = this.range ? (this.current ?? 1) + this.range + 1 : 0;

    for (let i = 1; i <= pageCount; i++) {
      if (i === 1 || i === pageCount || (!this.range || (i >= left && i < right))) {
        pageRange.push(i);
      }
    }

    let l = 0;

    for (const i of pageRange) {
      if (l) {
        if (i - l === 2) {
          pageRangeWithDots.push((l + 1).toString());
        } else if (i - l !== 1) {
          pageRangeWithDots.push('...');
        }
      }

      pageRangeWithDots.push(i.toString());

      l = i;
    }

    return pageRangeWithDots;
  }
}
